import { call, put, takeEvery, takeLatest } from 'redux-saga/effects'
import {
  AdminCreateAiDocumentReviewConfig,
  AdminDeleteAiDocumentReviewConfig,
  AdminFetchAiDocumentReviewConfigs,
  AdminUpdateAiDocumentReviewConfig,
} from 'src/constants/actionTypes'
import { Endpoints } from 'src/constants/endpoints'
import type {
  AiDocumentReviewConfig,
  AiDocumentReviewConfigCreatePayload,
  AiDocumentReviewConfigUpdatePayload,
} from 'src/interfaces/aiDocumentReviewConfig'
import { extractApiErrorMessages } from 'src/utils/errorHandlers'
import { logger } from 'src/utils/logger'
import { normalizeApiListPayload } from 'src/utils/normalizeApiListPayload'
import type { AsyncAction } from 'src/utils/reduxUtils'
import { callSecureApi } from './api/callApi'

interface FetchConfigsPayload {
  partnerId: string
}

interface UpdateConfigPayload {
  aiDocumentReviewConfigId: string
  values: AiDocumentReviewConfigUpdatePayload
}

interface DeleteConfigPayload {
  aiDocumentReviewConfigId: string
}

function resolveErrorMessage(error: unknown, fallback: string): string {
  const messages = extractApiErrorMessages(error)

  if (messages.length === 0) {
    return fallback
  }

  return messages.join(', ')
}

function* onFetchAiDocumentReviewConfigs(action: AsyncAction<FetchConfigsPayload>) {
  const partnerId = action.payload?.partnerId

  if (!partnerId) {
    yield put(AdminFetchAiDocumentReviewConfigs.failed('Missing partnerId'))

    if (action.next) {
      action.next('Missing partnerId')
    }

    return
  }

  try {
    const { payload }: { payload: unknown } = yield call(
      callSecureApi,
      Endpoints.AdminAiDocumentReviewConfigs,
      {
        method: 'GET',
        query: { partnerId },
      },
    )

    const configs = normalizeApiListPayload<AiDocumentReviewConfig>(payload)

    yield put(AdminFetchAiDocumentReviewConfigs.success(configs))

    if (action.next) {
      action.next(null, configs)
    }
  } catch (error: unknown) {
    logger.error('[saga.aiDocumentReviewConfig.onFetchAiDocumentReviewConfigs] error', error)

    yield put(AdminFetchAiDocumentReviewConfigs.failed(error))

    if (action.next) {
      action.next(resolveErrorMessage(error, 'Failed to load AI review configurations'))
    }
  }
}

function* onCreateAiDocumentReviewConfig(action: AsyncAction<AiDocumentReviewConfigCreatePayload>) {
  try {
    const { payload }: { payload: AiDocumentReviewConfig } = yield call(
      callSecureApi,
      Endpoints.AdminAiDocumentReviewConfigs,
      {
        method: 'POST',
        body: action.payload,
      },
    )

    yield put(AdminCreateAiDocumentReviewConfig.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (error: unknown) {
    logger.error('[saga.aiDocumentReviewConfig.onCreateAiDocumentReviewConfig] error', error)

    yield put(AdminCreateAiDocumentReviewConfig.failed(error))

    if (action.next) {
      action.next(resolveErrorMessage(error, 'Failed to create AI review configuration'))
    }
  }
}

function* onUpdateAiDocumentReviewConfig(action: AsyncAction<UpdateConfigPayload>) {
  const { aiDocumentReviewConfigId, values } = action.payload

  try {
    const { payload }: { payload: AiDocumentReviewConfig } = yield call(
      callSecureApi,
      Endpoints.AdminAiDocumentReviewConfig,
      {
        method: 'PUT',
        body: values,
        params: [{ field: ':aiDocumentReviewConfigId', value: aiDocumentReviewConfigId }],
      },
    )

    yield put(AdminUpdateAiDocumentReviewConfig.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (error: unknown) {
    logger.error('[saga.aiDocumentReviewConfig.onUpdateAiDocumentReviewConfig] error', error)

    yield put(AdminUpdateAiDocumentReviewConfig.failed(error))

    if (action.next) {
      action.next(resolveErrorMessage(error, 'Failed to update AI review configuration'))
    }
  }
}

function* onDeleteAiDocumentReviewConfig(action: AsyncAction<DeleteConfigPayload>) {
  const { aiDocumentReviewConfigId } = action.payload

  try {
    yield call(callSecureApi, Endpoints.AdminAiDocumentReviewConfig, {
      method: 'DELETE',
      returnResponse: true,
      params: [{ field: ':aiDocumentReviewConfigId', value: aiDocumentReviewConfigId }],
    })

    yield put(AdminDeleteAiDocumentReviewConfig.success({ aiDocumentReviewConfigId }))

    if (action.next) {
      action.next(null, { aiDocumentReviewConfigId })
    }
  } catch (error: unknown) {
    logger.error('[saga.aiDocumentReviewConfig.onDeleteAiDocumentReviewConfig] error', error)

    yield put(AdminDeleteAiDocumentReviewConfig.failed(error))

    if (action.next) {
      action.next(resolveErrorMessage(error, 'Failed to delete AI review configuration'))
    }
  }
}

export function* aiDocumentReviewConfigSaga() {
  yield takeLatest(AdminFetchAiDocumentReviewConfigs.type.REQUEST, onFetchAiDocumentReviewConfigs)
  yield takeEvery(AdminCreateAiDocumentReviewConfig.type.REQUEST, onCreateAiDocumentReviewConfig)
  yield takeEvery(AdminUpdateAiDocumentReviewConfig.type.REQUEST, onUpdateAiDocumentReviewConfig)
  yield takeEvery(AdminDeleteAiDocumentReviewConfig.type.REQUEST, onDeleteAiDocumentReviewConfig)
}
